import React, { useState, useRef } from 'react';
import { customFetch } from '../apiClient';

const MedicineImportModal = ({ isOpen, onClose, onSuccess }) => {
    const [file, setFile] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [importLog, setImportLog] = useState(null);
    const fileInputRef = useRef(null);

    if (!isOpen) return null;

    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0];
        if (selectedFile && !selectedFile.name.endsWith('.csv')) {
            setError("Only .csv files are allowed.");
            setFile(null);
            if (fileInputRef.current) fileInputRef.current.value = '';
        } else {
            setError("");
            setFile(selectedFile);
        }
    };

    const downloadTemplate = () => {
        const headers = ["Medicine Name", "Brand", "Category", "Composition", "Unit", "Pack Size", "GST%", "HSN Code"];
        const sampleRow = ["Dolo 650", "Micro Labs", "Antipyretic", "Paracetamol 650mg", "Strip", "15", "12", "3004"];
        
        const csvContent = headers.join(",") + "\n" + sampleRow.join(",");
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.setAttribute("href", url);
        link.setAttribute("download", "Medicine_Import_Template.csv");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleImport = async () => {
        if (!file) {
            setError("Please select a file first.");
            return;
        }

        setLoading(true);
        setError("");
        setImportLog(null);

        const formData = new FormData();
        formData.append("file", file);

        try {
            const res = await customFetch('/MedicineDT/import', {
                method: 'POST',
                body: formData 
            });

            const data = await res.json();
            
            if (!res.ok) throw new Error(data.message || data.error || "Failed to import file.");

            setImportLog({ message: data.message, errors: data.errors });
            setFile(null);
            if(fileInputRef.current) fileInputRef.current.value = '';
            
            // If no errors, auto close and trigger success after 2 seconds
            if (!data.errors || data.errors.length === 0) {
                 setTimeout(() => {
                    onSuccess();
                    onClose();
                 }, 2000);
            } else {
                 // Refresh data anyway since some rows might have been successful
                 onSuccess();
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="modal-overlay" onClick={onClose} style={{ zIndex: 1060 }}>
            <div className="modal-container" style={{ maxWidth: '500px', zIndex: 1061 }} onClick={e => e.stopPropagation()}>
                <div className="modal-card">
                    <div className="modal-header">
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', fontWeight: '600' }}>
                           <i className="bi bi-file-earmark-spreadsheet"></i> Import Medicines (CSV)
                        </div>
                        <button className="btn-modal-close" onClick={onClose}><i className="bi bi-x-lg"></i></button>
                    </div>
                    
                    <div className="modal-body" style={{ padding: '16px' }}>
                        {error && <div className="alert-custom alert-danger mb-3"><i className="bi bi-exclamation-circle-fill"></i> {error}</div>}
                        
                        <div className="info-box mb-3" style={{ background: '#f8fafc', padding: '12px', border: '1px solid #e2e8f0', borderRadius: '4px', fontSize: '11px', color: '#4a5568' }}>
                            <strong style={{ display: 'block', marginBottom: '4px', color: '#2d3748' }}>Important Rules:</strong>
                            <ul style={{ paddingLeft: '16px', margin: '0 0 8px 0', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                <li><strong>Medicine Name, Brand, and Category</strong> are mandatory.</li>
                                <li>If a Brand or Category doesn't exist, the system will auto-create it.</li>
                                <li>Duplicate medicine names will be safely skipped.</li>
                            </ul>
                            <button type="button" onClick={downloadTemplate} style={{ color: 'var(--theme-color, #0088cc)', border: 'none', background: 'none', cursor: 'pointer', padding: 0, fontWeight: '600', display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11px' }}>
                                <i className="bi bi-download"></i> Download CSV Template
                            </button>
                        </div>

                        <div className="form-group mb-2">
                            <label className="form-label">Select CSV File <span className="req">*</span></label>
                            <input 
                                type="file" 
                                accept=".csv" 
                                onChange={handleFileChange} 
                                ref={fileInputRef}
                                className="form-input" 
                                style={{ padding: '4px' }}
                                disabled={loading}
                            />
                        </div>

                        {importLog && (
                            <div className="mt-3" style={{ fontSize: '11px' }}>
                                <div className="alert-custom alert-success mb-2"><i className="bi bi-check-circle-fill"></i> {importLog.message}</div>
                                {importLog.errors?.length > 0 && (
                                    <div className="alert-custom alert-danger" style={{ maxHeight: '120px', overflowY: 'auto', display: 'block' }}>
                                        <strong style={{ display: 'block', marginBottom: '4px' }}>Warnings / Skipped Rows:</strong>
                                        <ul style={{ paddingLeft: '16px', margin: 0, display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                            {importLog.errors.map((e, idx) => <li key={idx}>{e}</li>)}
                                        </ul>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                    
                    <div className="modal-footer" style={{ background: '#f8fafc', borderTop: '1px solid #e2e8f0', padding: '10px 16px', display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                        <button type="button" className="btn-modal-cancel" onClick={onClose} disabled={loading} style={{ padding: '6px 12px', border: 'none', background: '#e2e8f0', color: '#4a5568', borderRadius: '4px', fontSize: '11px', fontWeight: '600', cursor: 'pointer' }}>
                            Cancel
                        </button>
                        <button type="button" className="btn-modal-submit" onClick={handleImport} disabled={loading || !file} style={{ padding: '6px 16px', border: 'none', background: 'var(--theme-color, #3182ce)', color: 'white', borderRadius: '4px', fontSize: '11px', fontWeight: '600', cursor: loading || !file ? 'not-allowed' : 'pointer', opacity: loading || !file ? 0.7 : 1 }}>
                            {loading ? <><span className="spinner-border spinner-border-sm" style={{ marginRight: '4px' }}></span> Processing...</> : <><i className="bi bi-upload"></i> Upload & Import</>}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MedicineImportModal;